// The Networks card of a customer: its networks with counts; for platform administrators also
// add, rename, move (empty networks only) and permanent delete. The delete dialog shows the
// server-computed impact and requires the exact network name; the server checks the name and that
// the preview still describes the network, and refuses a network that is a user's home network.
import { useState } from 'react';
import {
  ApiError,
  createNetwork,
  deleteNetwork,
  moveNetwork,
  NETWORK_NAME_MAX,
  networkNameValid,
  previewNetworkDelete,
  renameNetwork,
  type NetworkDeleteImpact,
  type NetworkSummary,
  type Tenant,
} from '../../services/tenancy';
import { deletedNotice, describeNetworkDelete, networkNameConfirmed } from './lifecycleModel';

const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm';
const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';
const linkBtn = 'text-xs disabled:opacity-40';

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.code ? `${err.message} (${err.code})` : err.message;
  return err instanceof Error ? err.message : 'The request could not be completed. Please try again.';
}

export function NetworkDeleteDialog({ impact, typed, busy, onTyped, onCancel, onConfirm }: {
  impact: NetworkDeleteImpact; typed: string; busy?: boolean;
  onTyped: (v: string) => void; onCancel: () => void; onConfirm: () => void;
}) {
  const d = describeNetworkDelete(impact);
  const ready = !d.blocked && networkNameConfirmed(typed, impact.network.name);
  return (
    <div data-management-modal role="dialog" aria-modal="true" aria-labelledby="network-delete-title" className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form className="bg-gray-800 border border-red-700 rounded-lg w-full max-w-lg p-5 space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (ready && !busy) onConfirm(); }}>
        <h3 id="network-delete-title" className="text-lg font-semibold text-red-200">{d.title}</h3>
        {d.blocked
          ? <p role="alert" className="text-sm text-yellow-200">{d.blocked}</p>
          : <ul className="list-disc pl-5 space-y-1 text-sm">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>}
        {!d.blocked && (
          <div>
            <label htmlFor="network-delete-confirm" className="block text-xs text-gray-300 mb-1">
              Type the network name <span className="font-mono text-gray-100">{impact.network.name}</span> to confirm
            </label>
            <input id="network-delete-confirm" className={`${input} w-full`} value={typed} autoComplete="off" autoFocus
              onChange={(e) => onTyped(e.target.value)} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={onCancel} disabled={busy}>Cancel</button>
          {!d.blocked && (
            <button type="submit" className={`${btn} bg-red-700 hover:bg-red-600`} disabled={!ready || busy}>
              {busy ? 'Deleting…' : d.confirm}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function NetworkRow({ network, tenant, tenants, platform, run, onNotice, onError }: {
  network: NetworkSummary; tenant: Tenant; tenants: Tenant[]; platform: boolean;
  run: (fn: () => Promise<unknown>) => Promise<void>; onNotice: (msg: string) => void; onError: (msg: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(network.name);
  const [impact, setImpact] = useState<NetworkDeleteImpact | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const n = network;
  const canSave = networkNameValid(name) && name.trim() !== n.name;

  const save = () => run(async () => { await renameNetwork(n.id, name.trim()); setEditing(false); });
  const openDelete = async () => {
    setBusy(true);
    onError(null);
    try {
      setTyped('');
      setImpact(await previewNetworkDelete(n.id));
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const confirmDelete = async () => {
    if (!impact) return;
    setBusy(true);
    try {
      const res = await deleteNetwork(n.id, typed.trim(), impact.fingerprint);
      setImpact(null);
      onNotice(deletedNotice('network', { network: res.impact }));
      await run(async () => undefined);
    } catch (err) {
      setImpact(null);
      // A refused delete (stale preview, homed users, ...) means the network changed: reload, then report.
      await run(async () => undefined);
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-t border-gray-700">
      <td className="py-1.5">
        {editing ? (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (canSave) save(); }}>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NETWORK_NAME_MAX} className={`${input} flex-1 min-w-0`}
              aria-label={`New name for ${n.name}`} autoFocus />
            <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!canSave}>Save</button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={() => { setEditing(false); setName(n.name); }}>Cancel</button>
          </form>
        ) : n.name}
      </td>
      <td className="py-1.5">{n.radio_count}</td>
      <td className="py-1.5">{n.group_count}</td>
      {platform && (
        <td className="py-1.5">
          <select value="" className={input} aria-label={`Move ${n.name}`} onChange={(e) => {
            const target = tenants.find((t) => t.id === e.target.value);
            if (target && window.confirm(`Move network "${n.name}" to customer "${target.name}"?

Only an empty network can move (its own groups and statuses only): no rented radios, users, department scopes or history.`)) {
              run(() => moveNetwork(n.id, target.id));
            }
          }}>
            <option value="">—</option>
            {tenants.filter((t) => t.id !== tenant.id).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </td>
      )}
      {platform && (
        <td className="py-1.5 text-right whitespace-nowrap space-x-3">
          {!editing && <button className={`${linkBtn} text-blue-300 hover:text-blue-200`} onClick={() => { setName(n.name); setEditing(true); }}>Edit</button>}
          <button className={`${linkBtn} text-red-400 hover:text-red-300`} disabled={busy} onClick={openDelete}>Delete…</button>
          {impact && <NetworkDeleteDialog impact={impact} typed={typed} busy={busy} onTyped={setTyped} onCancel={() => setImpact(null)} onConfirm={confirmDelete} />}
        </td>
      )}
    </tr>
  );
}

/** A customer's networks. Only platform administrators get the add/edit/move/delete controls. */
export default function CustomerNetworks({ tenant, tenants, platform, run, onNotice, onError }: {
  tenant: Tenant; tenants: Tenant[]; platform: boolean;
  run: (fn: () => Promise<unknown>) => Promise<void>; onNotice: (msg: string) => void; onError: (msg: string | null) => void;
}) {
  const [newNetwork, setNewNetwork] = useState('');
  return (
    <div className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
      <h3 className="font-semibold">Networks</h3>
      <table className="w-full text-sm text-left">
        <thead className="text-gray-400"><tr>
          <th className="py-1">Network</th><th className="py-1">Radios</th><th className="py-1">Groups</th>
          {platform && <th className="py-1">Move to customer</th>}
          {platform && <th className="py-1"><span className="sr-only">Actions</span></th>}
        </tr></thead>
        <tbody>
          {tenant.networks.map((n) => (
            <NetworkRow key={`${n.id}:${n.name}`} network={n} tenant={tenant} tenants={tenants} platform={platform} run={run} onNotice={onNotice} onError={onError} />
          ))}
          {tenant.networks.length === 0 && <tr><td colSpan={platform ? 5 : 3} className="py-2 text-gray-400">No networks</td></tr>}
        </tbody>
      </table>
      {platform && <div className="flex gap-2">
        <input value={newNetwork} onChange={(e) => setNewNetwork(e.target.value)} placeholder="New network name" maxLength={NETWORK_NAME_MAX} className={`${input} flex-1`} />
        <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!networkNameValid(newNetwork)}
          onClick={() => run(async () => { await createNetwork(newNetwork.trim(), tenant.id); setNewNetwork(''); })}>Add network</button>
      </div>}
      {platform && <p className="text-xs text-gray-400">
        Deleting a network returns its radios to the rental inventory and deletes its groups and network data. A network that is still a user&apos;s home network cannot be deleted.
      </p>}
    </div>
  );
}
